import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ListResponse, ResultDetail, ResultOverview, SearchOptions, TrainingResult } from '@ca/core/models';
import { Observable } from 'rxjs';
import { retry } from '../../../../core/decorators/retry';
import { ApplicationType } from '../../../../core/enums/application-type.enum';
import { errorContextToken } from '../../../../core/interceptors/global-error.interceptor';
import {
    ApplicationDetail,
    ApplicationOverview,
    CreateApplicationPayload,
    EditPublishedApplicationPayload,
    PublishCustomizedApplicationPayload,
} from '../../../../core/models/application.model';
import { ErrorContext } from '../../../../core/models/error-context.model';

@Injectable({
    providedIn: 'root',
})
export class ApplicationsService {
    private readonly API_URL = '/api/documents/applications';
    private readonly HEADERS = { 'Content-Type': 'application/json' };

    constructor(protected http: HttpClient) {}

    @retry()
    searchApplications(searchOptions: SearchOptions, type: ApplicationType): Observable<ListResponse<ApplicationOverview>> {
        let params = new HttpParams();
        params = params.append('page[number]', searchOptions.pageNumber.toString());
        params = params.append('page[size]', searchOptions.pageSize.toString());
        params = params.append('sort', searchOptions.sortBy);
        params = params.append('text', searchOptions.searchTerm);
        return this.http.get<ListResponse<ApplicationOverview>>(`${this.API_URL}/${type === ApplicationType.Public ? 'public' : 'private'}`, {
            headers: this.HEADERS,
            params,
        });
    }

    @retry()
    getApplicationById$(id): Observable<ApplicationDetail> {
        return this.http.get<ApplicationDetail>(`${this.API_URL}/${id}`, { headers: this.HEADERS });
    }

    createApplication$(payload: CreateApplicationPayload): Observable<ApplicationDetail> {
        return this.http.post<ApplicationDetail>(`${this.API_URL}`, payload, {
            headers: this.HEADERS,
        });
    }

    deleteApplication$(id: string): Observable<void> {
        const errorContext: ErrorContext = { isEnabled: true, errors: [{ code: 500, key: 'APPLICATIONS.DELETE_APPLICATION_ERROR' }] };
        return this.http.delete<void>(`${this.API_URL}/${id}`, {
            headers: this.HEADERS,
            context: new HttpContext().set(errorContextToken, errorContext),
        });
    }

    publishApplication$(payload: PublishCustomizedApplicationPayload): Observable<void> {
        const errorContext: ErrorContext = { isEnabled: true, errors: [{ code: 500, key: 'APPLICATIONS.PUBLISH_CUSTOMIZED_APPLICATION.ERRORS.CREATE' }] };
        return this.http.post<void>(`${this.API_URL}/publish`, payload, {
            headers: this.HEADERS,
            context: new HttpContext().set(errorContextToken, errorContext),
        });
    }

    unPublishApplication$(applicationId: string): Observable<void> {
        const errorContext: ErrorContext = { isEnabled: true, errors: [{ code: 500, key: 'APPLICATIONS.UNPUBLISH_CUSTOMIZED_APPLICATION.ERRORS.UNPUBLISH' }] };
        return this.http.post<void>(`${this.API_URL}/unpublish/${applicationId}`, {
            headers: this.HEADERS,
            context: new HttpContext().set(errorContextToken, errorContext),
        });
    }

    editApplication$(payload: EditPublishedApplicationPayload): Observable<void> {
        const errorContext: ErrorContext = { isEnabled: true, errors: [{ code: 500, key: 'APPLICATIONS.PUBLISH_CUSTOMIZED_APPLICATION.ERRORS.EDIT' }] };
        return this.http.post<void>(`${this.API_URL}/edit`, payload, {
            headers: this.HEADERS,
            context: new HttpContext().set(errorContextToken, errorContext),
        });
    }

    searchTrainingResults(searchOptions: SearchOptions): Observable<ListResponse<TrainingResult>> {
        const errorContext: ErrorContext = { isEnabled: true, errors: [{ code: 500, key: 'APPLICATIONS.GET_APPLICATIONS_RESULTS_ERROR' }] };
        const params = new HttpParams()
            .set('page[number]', searchOptions.pageNumber.toString())
            .set('page[size]', searchOptions.pageSize.toString())
            .set('sort', searchOptions.sortBy)
            .set('search', searchOptions.searchTerm)
            .set('text', searchOptions.searchTerm)
            .set('users', searchOptions.users ?? '')
            .set('state', searchOptions.state ?? '');
        return this.http.get<ListResponse<TrainingResult>>(`${this.API_URL}/monitoring/search`, {
            headers: this.HEADERS,
            params,
            context: new HttpContext().set(errorContextToken, errorContext),
        });
    }

    searchSegmentationResults(searchOptions: SearchOptions, applicationId: string | null = null): Observable<ListResponse<ResultOverview>> {
        const errorContext: ErrorContext = { isEnabled: true, errors: [{ code: 500, key: 'APPLICATIONS.GET_APPLICATIONS_RESULTS_ERROR' }] };
        let params = new HttpParams();
        params = params.append('page[number]', searchOptions.pageNumber.toString());
        params = params.append('page[size]', searchOptions.pageSize.toString());
        params = params.append('sort', searchOptions.sortBy);
        params = params.append('text', searchOptions.searchTerm);
        if (applicationId) {
            params = params.append('applicationId', applicationId);
        }

        return this.http.get<ListResponse<ResultOverview>>(`${this.API_URL}/results/search`, {
            headers: this.HEADERS,
            params,
            context: new HttpContext().set(errorContextToken, errorContext),
        });
    }

    getResultById$(resultd: string): Observable<ResultDetail> {
        const params = new HttpParams().set('access', 'read');
        return this.http.get<ResultDetail>(`${this.API_URL}/results/${resultd}`, { headers: this.HEADERS, params: params });
    }
}
